import type { PunchAction, Shift, Status } from "./types";

/** Lokalt datum (inte UTC) — panelen arbetar alltid i enhetens tidszon. */
export function localDate(date: Date = new Date()): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;
}

export function statusOf(shift?: Shift | null): Status {
  if (!shift || !shift.check_in) return "out";
  if (shift.check_out) return "done";
  if (shift.lunch_start && !shift.lunch_end) return "lunch";
  return "working";
}

/** Vilka stämplingar som är tillåtna härnäst. Används av både panelen och servern. */
export function nextActions(shift?: Shift | null): PunchAction[] {
  const status = statusOf(shift);
  if (status === "out") return ["check_in"];
  if (status === "lunch") return ["lunch_end"];
  if (status === "done") return [];
  return shift?.lunch_end ? ["check_out"] : ["lunch_start", "check_out"];
}

/**
 * Ett pass från en tidigare dag som aldrig stämplades ut. Det kan inte avslutas
 * från panelen — en ny dag får ett nytt pass — så det måste rättas i adminläget.
 * Räknas inte som arbetad tid: `workedMinutes` skulle annars ticka vidare i
 * dagar och ge ett pass på 170 timmar.
 */
export function missedCheckout(shift: Shift | null | undefined, today: string = localDate()) {
  return !!shift?.check_in && !shift.check_out && shift.work_date < today;
}

/** Arbetade minuter exklusive lunch. Öppna skift räknas fram till `now`. */
export function workedMinutes(shift: Shift | null | undefined, now: Date = new Date()): number {
  if (!shift?.check_in) return 0;
  const start = new Date(shift.check_in).getTime();
  const end = shift.check_out ? new Date(shift.check_out).getTime() : now.getTime();
  let ms = Math.max(0, end - start);

  if (shift.lunch_start) {
    const lunchStart = new Date(shift.lunch_start).getTime();
    const lunchEnd = shift.lunch_end ? new Date(shift.lunch_end).getTime() : end;
    ms -= Math.max(0, Math.min(lunchEnd, end) - lunchStart);
  }

  return Math.max(0, Math.round(ms / 60000));
}

export function lunchMinutes(shift: Shift | null | undefined, now: Date = new Date()): number {
  if (!shift?.lunch_start) return 0;
  const start = new Date(shift.lunch_start).getTime();
  const end = shift.lunch_end
    ? new Date(shift.lunch_end).getTime()
    : shift.check_out
      ? new Date(shift.check_out).getTime()
      : now.getTime();
  return Math.max(0, Math.round((end - start) / 60000));
}

export function formatClock(iso: string | null): string {
  if (!iso) return "--:--";
  const date = new Date(iso);
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

/** `YYYY-MM-DD` → "fredag 25 sep". Tolkas som lokalt datum, inte UTC. */
export function formatWorkDate(workDate: string): string {
  const [year, month, day] = workDate.split("-").map(Number);
  return new Intl.DateTimeFormat("sv-SE", {
    weekday: "long",
    day: "numeric",
    month: "short",
  }).format(new Date(year, month - 1, day));
}

/** t.ex. "7 h 30 min" */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  return `${hours} h ${String(rest).padStart(2, "0")} min`;
}

/** Decimaltimmar för löneexport, t.ex. "7.50". */
export function decimalHours(minutes: number): string {
  return (minutes / 60).toFixed(2);
}

/**
 * Saldo i timmar med tecken, t.ex. "+2,5 h" eller "−1,25 h". Tecknet är hela
 * poängen med ett flexsaldo, så det skrivs ut även när saldot är noll.
 */
export function formatBalance(hours: number): string {
  const rounded = Math.round(hours * 100) / 100;
  // Riktigt minustecken, inte bindestreck.
  const sign = rounded < 0 ? "−" : "+";
  const value = Math.abs(rounded).toLocaleString("sv-SE", { maximumFractionDigits: 2 });
  return `${sign}${value} h`;
}

/**
 * Kort datum för när ett saldo hämtades, t.ex. "14 aug 16:20". Saldona från
 * Fortnox visas alltid tillsammans med den här tidpunkten — de är bara sanna då.
 */
export function formatStamp(value: string): string {
  return new Intl.DateTimeFormat("sv-SE", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}
